import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertCircle, Check, CheckCircle2, ChevronsUpDown, FileText, FileUp, Loader2, Plus, StickyNote, Trash2, Upload, X as XIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { createAdminProduct, fetchAdminProducts } from "@/lib/adminProductsApi";
import { fetchCatalogMetadata } from "@/lib/catalogApi";
import type { CatalogMetadata } from "@/lib/catalogApi";
import { ProductDialog, emptyDraft, type DraftProduct } from "@/components/admin/ProductDialog";
import {
  confirmOrderImport,
  previewOrderImport,
  type ItemPreview,
  type OrderImportEnvelope,
  type OrderImportMessage,
  type OrderImportPreview,
  type PartyChoice,
} from "@/lib/adminOrderImportApi";
import { ApiError } from "@/lib/api";
import { formatCnpj } from "@/lib/format";
import { isValidCnpj, lookupCnpj, normalizeCnpj } from "@/lib/cnpj";
import type { AdminProductSummary, ClientType } from "@/lib/types";

type CustomerSelection = "BILLING" | "CHARGE" | "NEW";
type UnitSelection = "EXISTING" | "NEW";

interface ItemDecision {
  catalogProductId: string;
  rememberMapping: boolean;
  quantityOverride?: number | null;
  cut?: string | null;
  /** Nome da unidade escolhida. `undefined` = nunca tocado (auto-preenche do arquivo/padrão). */
  unit?: string | null;
  /** Observação livre por item, preenchida manualmente na importação. */
  observation?: string;
}

function parseQuantityInput(raw: string): number | null {
  const cleaned = raw.trim().replace(/\./g, "").replace(",", ".");
  if (cleaned === "") return null;
  const value = Number(cleaned);
  return Number.isFinite(value) ? value : null;
}

function effectiveQuantity(item: ItemPreview, decision: ItemDecision | undefined): number | null {
  if (decision && decision.quantityOverride !== undefined) return decision.quantityOverride;
  return item.quantity ?? null;
}

interface NewCustomerForm {
  email: string;
  companyName: string;
  tradeName: string;
  cnpj: string;
  phone: string;
  contactName: string;
  address: string;
  clientType: ClientType;
}

interface NewUnitForm {
  externalCode: string;
  name: string;
  address: string;
  district: string;
  city: string;
  postalCode: string;
  deliveryWindowText: string;
  email: string;
}

const CLIENT_TYPES: { value: ClientType; label: string }[] = [
  { value: "RESTAURANTE", label: "Restaurante" },
  { value: "SUPERMERCADO", label: "Supermercado" },
  { value: "COZINHA_INDUSTRIAL", label: "Cozinha industrial" },
];

function emptyCustomerForm(party: PartyChoice | undefined): NewCustomerForm {
  return {
    email: "",
    // Prefer the resolved customer name (from CNPJ lookup) when the file itself doesn't
    // carry a name — keeps the manual-cadastro form aligned with the party card.
    companyName: party?.companyName ?? party?.existingCustomerName ?? "",
    tradeName: "",
    cnpj: party?.cnpj ? formatCnpj(party.cnpj) : "",
    phone: "",
    contactName: "",
    address: party?.address ?? "",
    clientType: "COZINHA_INDUSTRIAL",
  };
}

function emptyUnitForm(unit: OrderImportPreview["deliveryUnit"]): NewUnitForm {
  return {
    externalCode: unit.externalCode ?? "",
    name: unit.name ?? "",
    address: unit.address ?? "",
    district: unit.district ?? "",
    city: unit.city ?? "",
    postalCode: unit.postalCode ?? "",
    deliveryWindowText: unit.deliveryWindowText ?? "",
    email: unit.email ?? "",
  };
}

function formatDate(value: string | null | undefined): string {
  if (!value) return "—";
  const [y, m, d] = value.split("-");
  return `${d}/${m}/${y}`;
}

function partyHasData(p: PartyChoice): boolean {
  return Boolean(p.cnpj || p.companyName);
}

interface OrderImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImported?: (orderId: string) => void;
}

export default function OrderImportDialog({ open, onOpenChange, onImported }: OrderImportDialogProps) {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [products, setProducts] = useState<AdminProductSummary[]>([]);
  const [productsLoading, setProductsLoading] = useState(true);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  /** Full envelope returned by /import/preview. Drives the wizard when it carries >1 message. */
  const [envelope, setEnvelope] = useState<OrderImportEnvelope | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  /** Per-message outcome so the stepper can show ✓ (imported) / — (skipped) / pending. */
  const [messageStatus, setMessageStatus] = useState<Record<number, "imported" | "skipped" | "pending">>({});
  /** Currently-rendered single-message payload (derived from envelope[currentIndex]). */
  const [preview, setPreview] = useState<OrderImportPreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const [customerSelection, setCustomerSelection] = useState<CustomerSelection>("BILLING");
  const [newCustomer, setNewCustomer] = useState<NewCustomerForm>(emptyCustomerForm(undefined));

  const [unitSelection, setUnitSelection] = useState<UnitSelection>("EXISTING");
  const [newUnit, setNewUnit] = useState<NewUnitForm>(
    emptyUnitForm({ externalCode: null, name: null, address: null, district: null, city: null, postalCode: null, deliveryWindowText: null, email: null, existingId: null }),
  );

  const [decisions, setDecisions] = useState<Record<number, ItemDecision>>({});
  const [removedLines, setRemovedLines] = useState<Set<number>>(new Set());
  const [productModal, setProductModal] = useState<{ lineNumber: number; suggestedName: string } | null>(null);
  const [catalogMetadata, setCatalogMetadata] = useState<CatalogMetadata | null>(null);
  const [overwriteExisting, setOverwriteExisting] = useState(false);
  const [notes, setNotes] = useState("");
  /** Confirmation gate for importing an ALTERACAO=S AF through the new-AF flow. */
  const [alterationConfirmOpen, setAlterationConfirmOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    let mounted = true;
    setProductsLoading(true);
    fetchAdminProducts()
      .then((list) => {
        if (mounted) setProducts(list.filter((p) => p.active));
      })
      .catch((err) => {
        toast.error(err instanceof Error ? err.message : "Não foi possível carregar produtos");
      })
      .finally(() => {
        if (mounted) setProductsLoading(false);
      });
    fetchCatalogMetadata()
      .then((meta) => {
        if (mounted) setCatalogMetadata(meta);
      })
      .catch(() => {
        // metadata is only used in the create-product modal — silent failure OK
      });
    return () => {
      mounted = false;
    };
  }, [open]);

  // Reset state when the dialog closes so the next open starts fresh.
  useEffect(() => {
    if (!open) {
      setSelectedFile(null);
      setEnvelope(null);
      setCurrentIndex(0);
      setMessageStatus({});
      setPreview(null);
      setDecisions({});
      setRemovedLines(new Set());
      setProductModal(null);
      setOverwriteExisting(false);
      setNotes("");
      setIsDragging(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }, [open]);

  const productOptions = useMemo(
    () => [...products].sort((a, b) => a.name.localeCompare(b.name)),
    [products],
  );

  const categoryOptions = useMemo(
    () =>
      (catalogMetadata?.categories ?? []).map((c) => ({
        id: c.code,
        label: c.label,
        emoji: c.emoji,
      })),
    [catalogMetadata],
  );

  const cutOptions = useMemo(
    () => (catalogMetadata?.cutTypes ?? []).map((c) => c.name),
    [catalogMetadata],
  );

  const currentMessage: OrderImportMessage | null = envelope?.messages[currentIndex] ?? null;
  const totalMessages = envelope?.messages.length ?? 0;
  const showWizard = totalMessages > 1;

  const reset = () => {
    setSelectedFile(null);
    setEnvelope(null);
    setCurrentIndex(0);
    setMessageStatus({});
    setPreview(null);
    setDecisions({});
    setOverwriteExisting(false);
    setNotes("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const onFileSelected = (file: File | null) => {
    setSelectedFile(file);
    setEnvelope(null);
    setCurrentIndex(0);
    setMessageStatus({});
    setPreview(null);
    setDecisions({});
  };

  /**
   * Loads a message into the editable form. Called whenever the wizard's currentIndex changes
   * (initial preview load and every "Próximo"/"Pular"). For non-VALID messages we still call
   * this so the form clears — the UI then renders the WRONG_FLOW / PARSE_ERROR panel instead
   * of the editor.
   */
  const applyMessageToForms = (msg: OrderImportMessage) => {
    setOverwriteExisting(false);
    setNotes("");
    setDecisions({});

    if (msg.status !== "VALID" || !msg.payload) {
      setPreview(null);
      return;
    }
    const result = msg.payload;
    setPreview(result);

    const initialDecisions: Record<number, ItemDecision> = {};
    result.items.forEach((item) => {
      if (item.mappedProductId) {
        initialDecisions[item.lineNumber] = {
          catalogProductId: item.mappedProductId,
          // Quando o item veio de um mapeamento lembrado, já marca "mapear sempre" para que,
          // se o operador apenas confirmar, a preferência seja mantida (e o BE detecta no-op).
          rememberMapping: item.mappingRemembered === true,
          cut: item.suggestedCut ?? null,
          unit: item.mappedUnitName ?? undefined,
        };
      }
    });
    setDecisions(initialDecisions);

    const billingHasMatch = Boolean(result.billing.existingCustomerId);
    const chargeHasMatch = Boolean(result.charge.existingCustomerId);
    const initialSelection: CustomerSelection = billingHasMatch
      ? "BILLING"
      : chargeHasMatch
      ? "CHARGE"
      : partyHasData(result.billing)
      ? "BILLING"
      : "CHARGE";
    setCustomerSelection(billingHasMatch || chargeHasMatch ? initialSelection : "NEW");
    setNewCustomer(emptyCustomerForm(
      initialSelection === "CHARGE" ? result.charge : result.billing,
    ));

    setUnitSelection(result.deliveryUnit.existingId ? "EXISTING" : "NEW");
    setNewUnit(emptyUnitForm(result.deliveryUnit));
  };

  const onRunPreview = async () => {
    if (!selectedFile) return;
    setPreviewing(true);
    try {
      const env = await previewOrderImport(selectedFile);
      if (env.messages.length === 0) {
        toast.error("Nenhuma mensagem encontrada no arquivo.");
        return;
      }
      setEnvelope(env);
      setCurrentIndex(0);
      setMessageStatus(
        Object.fromEntries(env.messages.map((m) => [m.index, "pending" as const])),
      );
      applyMessageToForms(env.messages[0]);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Falha ao processar arquivo");
    } finally {
      setPreviewing(false);
    }
  };

  /** Move to the next message in the wizard; if we're past the end, close the dialog. */
  const goToMessage = (nextIndex: number) => {
    if (!envelope) return;
    if (nextIndex >= envelope.messages.length) {
      // All messages processed — summarise and close.
      const imported = Object.values(messageStatus).filter((s) => s === "imported").length;
      const skipped = Object.values(messageStatus).filter((s) => s === "skipped").length;
      if (imported > 0) {
        toast.success(
          `Importação concluída: ${imported} pedido(s)${skipped > 0 ? `, ${skipped} pulado(s)` : ""}.`,
        );
      } else if (skipped > 0) {
        toast.info("Nenhum pedido importado.");
      }
      onOpenChange(false);
      return;
    }
    setCurrentIndex(nextIndex);
    applyMessageToForms(envelope.messages[nextIndex]);
  };

  /** Skip the current message (WRONG_FLOW, PARSE_ERROR, or operator choice) and advance. */
  const skipCurrent = () => {
    if (!envelope) return;
    const idx = envelope.messages[currentIndex].index;
    setMessageStatus((prev) => ({ ...prev, [idx]: "skipped" }));
    goToMessage(currentIndex + 1);
  };

  const updateDecision = (lineNumber: number, patch: Partial<ItemDecision>) => {
    setDecisions((prev) => {
      const current = prev[lineNumber] ?? { catalogProductId: "", rememberMapping: false };
      return { ...prev, [lineNumber]: { ...current, ...patch } };
    });
  };

  const blockingErrors = useMemo(() => {
    if (!preview) return [];
    const items = preview.items.filter((it) => !removedLines.has(it.lineNumber));
    const errs: string[] = [...preview.errors];
    if (preview.existingAuthorization && !overwriteExisting) {
      errs.push(`AF ${preview.authorizationNumber} já importada — marque 'sobrescrever' para substituir.`);
    }
    if (customerSelection === "NEW" && !newCustomer.email.trim()) {
      errs.push("Informe o e-mail do novo cliente.");
    }
    if (customerSelection === "BILLING" && !preview.billing.existingCustomerId && !partyHasData(preview.billing)) {
      errs.push("Dados de faturamento ausentes — escolha cobrança ou cadastre um novo cliente.");
    }
    if (customerSelection === "CHARGE" && !preview.charge.existingCustomerId && !partyHasData(preview.charge)) {
      errs.push("Dados de cobrança ausentes — escolha faturamento ou cadastre um novo cliente.");
    }
    if (unitSelection === "NEW" && !newUnit.externalCode.trim()) {
      errs.push("Informe o código da unidade de entrega.");
    }
    if (items.length === 0) {
      errs.push("Nenhum item para importar — adicione produtos ou cancele.");
    }
    items.forEach((item) => {
      const decision = decisions[item.lineNumber];
      if (!decision || !decision.catalogProductId) {
        errs.push(`Linha ${item.lineNumber}: selecione um produto do catálogo.`);
      } else {
        const product = products.find((p) => p.id === decision.catalogProductId);
        const unitOptions = product
          ? Array.from(new Set([product.defaultUnit, ...(product.availableUnits ?? [])]))
          : [];
        if (unitOptions.length > 0 && !decision.unit) {
          errs.push(`Linha ${item.lineNumber}: selecione a unidade.`);
        }
      }
      const qty = effectiveQuantity(item, decision);
      if (qty == null || qty <= 0) {
        errs.push(`Linha ${item.lineNumber}: informe uma quantidade válida.`);
      }
    });
    return errs;
  }, [preview, customerSelection, newCustomer.email, unitSelection, newUnit.externalCode, decisions, overwriteExisting, removedLines, products]);

  // Confirm button gate: an ALTERACAO=S AF imported as new requires an explicit confirmation
  // (modified AF where a new AF was expected) before the actual import runs.
  const handleConfirmClick = () => {
    if (!preview || blockingErrors.length > 0) return;
    if (currentMessage?.alterationWarning) {
      setAlterationConfirmOpen(true);
      return;
    }
    void doConfirm();
  };

  const doConfirm = async () => {
    if (!preview || blockingErrors.length > 0) return;
    setSubmitting(true);

    const customer = (() => {
      if (customerSelection === "BILLING" && preview.billing.existingCustomerId) {
        return { existingCustomerId: preview.billing.existingCustomerId, newCustomer: null };
      }
      if (customerSelection === "CHARGE" && preview.charge.existingCustomerId) {
        return { existingCustomerId: preview.charge.existingCustomerId, newCustomer: null };
      }
      // NEW, or BILLING/CHARGE chosen but that party isn't cadastrado yet. In every one of these
      // cases the operator edits the same `newCustomer` form (see render condition below), so its
      // values are the source of truth — do NOT fall back to the file seed, that silently dropped
      // everything the operator typed except the e-mail.
      return {
        existingCustomerId: null,
        newCustomer: {
          email: newCustomer.email.trim(),
          companyName: newCustomer.companyName.trim() || null,
          tradeName: newCustomer.tradeName.trim() || null,
          // Envia só dígitos: o BE normaliza, mas mandar limpo evita reincidir a máscara salva.
          cnpj: newCustomer.cnpj.replace(/\D/g, "") || null,
          phone: newCustomer.phone.trim() || null,
          contactName: newCustomer.contactName.trim() || null,
          address: newCustomer.address.trim() || null,
          clientType: newCustomer.clientType,
        },
      };
    })();

    const deliveryUnit = unitSelection === "EXISTING" && preview.deliveryUnit.existingId
      ? { existingDeliveryUnitId: preview.deliveryUnit.existingId, newDeliveryUnit: null }
      : {
          existingDeliveryUnitId: null,
          newDeliveryUnit: {
            externalCode: newUnit.externalCode.trim(),
            name: newUnit.name.trim() || newUnit.externalCode.trim(),
            address: newUnit.address || null,
            district: newUnit.district || null,
            city: newUnit.city || null,
            postalCode: newUnit.postalCode || null,
            deliveryWindowText: newUnit.deliveryWindowText || null,
            email: newUnit.email.trim() || null,
          },
        };

    if (!selectedFile) {
      toast.error("Arquivo não disponível — reabra o diálogo e reenvie.");
      return;
    }
    try {
      const result = await confirmOrderImport(selectedFile, {
        authorizationNumber: preview.authorizationNumber,
        overwriteExisting,
        deliveryDate: preview.deliveryDate ?? "",
        notes: notes.trim() || null,
        customer,
        deliveryUnit,
        items: preview.items
          .filter((item) => !removedLines.has(item.lineNumber))
          .map((item) => {
            const decision = decisions[item.lineNumber];
            const qty = effectiveQuantity(item, decision);
            return {
              externalCode: item.externalCode,
              descriptionFromFile: item.descriptionFromFile,
              quantity: qty ?? 0,
              catalogProductId: decision?.catalogProductId ?? "",
              catalogUnitId: null,
              unitName: decision?.unit ?? null,
              cut: decision?.cut ?? null,
              observation: decision?.observation?.trim() || null,
              rememberMapping: decision?.rememberMapping ?? false,
            };
          }),
      });
      toast.success(`Pedido importado (AF ${preview.authorizationNumber})`);
      onImported?.(result.id);

      // Mark current message as imported and advance the wizard. If this was the only
      // message (or the last pending one) goToMessage will close the dialog. We deliberately
      // do NOT navigate to /admin/operacoes mid-wizard — the user is still confirming the
      // remaining messages. The hand-off to /admin/operacoes is the responsibility of the
      // page that hosts the dialog (Operations re-loads via onImported).
      if (envelope) {
        const idx = envelope.messages[currentIndex].index;
        setMessageStatus((prev) => ({ ...prev, [idx]: "imported" }));
        const nextIndex = currentIndex + 1;
        if (nextIndex >= envelope.messages.length) {
          // Last message — close. If we had only one and there's no onImported wired,
          // jump straight to the imported order; otherwise the parent already refreshed.
          if (!onImported && envelope.messages.length === 1) {
            navigate(`/admin/operacoes?orderId=${result.id}`);
          }
          onOpenChange(false);
        } else {
          goToMessage(nextIndex);
        }
      } else {
        onOpenChange(false);
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Falha ao confirmar importação");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0] ?? null;
    if (!file) return;
    const name = file.name.toLowerCase();
    const isAccepted =
      file.type === "application/xml" ||
      file.type === "text/xml" ||
      name.endsWith(".xml");
    if (isAccepted) {
      onFileSelected(file);
    } else {
      toast.error("Envie um arquivo .xml.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl gap-0 overflow-hidden p-0 sm:max-w-5xl">
        <DialogHeader className="border-b px-6 py-4">
          <DialogTitle className="flex items-center gap-2 text-xl">
            <FileUp className="h-5 w-5" /> Importar pedido
          </DialogTitle>
        </DialogHeader>

        <div className="max-h-[calc(90vh-8rem)] space-y-5 overflow-y-auto px-6 py-5">
          {envelope && (
            <div className="flex items-center justify-between gap-3">
              {showWizard ? (
                <div className="flex flex-1 flex-wrap items-center gap-2">
                  <Badge variant="outline" className="font-mono">
                    Pedido {currentIndex + 1} de {totalMessages}
                  </Badge>
                  {currentMessage?.externalOrderNumber && (
                    <span className="text-sm font-medium">AF {currentMessage.externalOrderNumber}</span>
                  )}
                  <div className="ml-2 flex items-center gap-1">
                    {envelope.messages.map((m) => {
                      const status = messageStatus[m.index] ?? "pending";
                      const isCurrent = m.index === currentMessage?.index;
                      return (
                        <span
                          key={m.index}
                          title={`Pedido ${m.externalOrderNumber ?? "?"} — ${status}`}
                          className={`h-2 w-2 rounded-full ${
                            isCurrent
                              ? "ring-2 ring-primary ring-offset-1"
                              : ""
                          } ${
                            status === "imported"
                              ? "bg-emerald-500"
                              : status === "skipped"
                              ? "bg-muted-foreground/40"
                              : "bg-primary/30"
                          }`}
                        />
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div />
              )}
              <Button variant="outline" size="sm" onClick={reset}>
                <Trash2 className="h-4 w-4" /> Recomeçar
              </Button>
            </div>
          )}

          {envelope && currentMessage && currentMessage.status !== "VALID" && (
            <AlertBox
              tone={currentMessage.status === "WRONG_FLOW" ? "warning" : "error"}
              title={
                currentMessage.status === "WRONG_FLOW"
                  ? "Esta mensagem está no fluxo errado"
                  : "Falha ao interpretar esta mensagem"
              }
            >
              <p className="text-sm">
                {currentMessage.status === "WRONG_FLOW"
                  ? currentMessage.wrongFlowReason
                  : currentMessage.errorMessage}
              </p>
              <div className="mt-3 flex gap-2">
                <Button onClick={skipCurrent} size="sm">
                  {currentIndex + 1 < totalMessages ? "Pular e ir ao próximo" : "Pular e fechar"}
                </Button>
              </div>
            </AlertBox>
          )}

          {!envelope && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept="application/xml,text/xml,.xml"
                className="hidden"
                onChange={(e) => onFileSelected(e.target.files?.[0] ?? null)}
              />
              <div
                onDragOver={handleDragOver}
                onDragEnter={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => !selectedFile && fileInputRef.current?.click()}
                className={`relative flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed px-6 py-12 text-center transition-colors ${
                  isDragging
                    ? "border-primary bg-primary/10"
                    : selectedFile
                    ? "border-primary/40 bg-secondary/30"
                    : "cursor-pointer border-border hover:border-primary/60 hover:bg-secondary/40"
                }`}
              >
                {selectedFile ? (
                  <>
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                      <FileText className="h-6 w-6" />
                    </div>
                    <div className="space-y-1">
                      <div className="font-medium">{selectedFile.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {(selectedFile.size / 1024).toFixed(1)} KB · XML
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-3 top-3 h-7 w-7 p-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        onFileSelected(null);
                        if (fileInputRef.current) fileInputRef.current.value = "";
                      }}
                      aria-label="Remover arquivo"
                    >
                      <XIcon className="h-4 w-4" />
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-secondary text-muted-foreground">
                      <Upload className="h-6 w-6" />
                    </div>
                    <div className="space-y-1">
                      <div className="font-medium">
                        Arraste o arquivo aqui ou <span className="text-primary underline-offset-4 hover:underline">clique para selecionar</span>
                      </div>
                      <div className="text-xs text-muted-foreground">Arquivos .xml</div>
                    </div>
                  </>
                )}
              </div>

              <div className="flex justify-end">
                <Button onClick={onRunPreview} disabled={!selectedFile || previewing} size="lg">
                  {previewing ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />}
                  Importar pedido
                </Button>
              </div>
            </>
          )}

      {preview && (
        <>
          <Card>
            <CardContent className="pt-6">
              <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
                <Field label="AF" value={preview.authorizationNumber} />
                <Field label="Data de entrega" value={formatDate(preview.deliveryDate)} />
                <Field label="Itens" value={String(preview.items.length)} />
              </div>
            </CardContent>
          </Card>

          {currentMessage?.alterationWarning && (
            <AlertBox tone="warning" title="AF marcada como alteração (ALTERACAO=S)">
              <p className="text-sm">{currentMessage.alterationWarning}</p>
            </AlertBox>
          )}

          {(preview.errors.length > 0 || !preview.deliveryDateConsistent) && (
            <AlertBox tone="error" title="Erros bloqueantes">
              <ul className="list-disc pl-5 text-sm">
                {!preview.deliveryDateConsistent && (
                  <li>Itens com datas de entrega divergentes. Ajuste o XML antes de importar.</li>
                )}
                {preview.errors.map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            </AlertBox>
          )}

          {preview.warnings.length > 0 && (
            <AlertBox tone="warning" title="Avisos">
              <ul className="list-disc pl-5 text-sm">
                {preview.warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            </AlertBox>
          )}

          {preview.existingAuthorization && (
            <AlertBox tone="warning" title="AF já importada">
              <div className="space-y-2 text-sm">
                <div>
                  Pedido existente: <strong>{preview.existingAuthorization.clientName}</strong>{" "}
                  <Badge variant="outline">{preview.existingAuthorization.status}</Badge>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={overwriteExisting}
                    onCheckedChange={(v) => setOverwriteExisting(v === true)}
                  />
                  Sobrescrever (cancela o pedido anterior e cria um novo)
                </label>
              </div>
            </AlertBox>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Cliente</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-3 md:grid-cols-2">
                <PartyOption
                  selected={customerSelection === "BILLING"}
                  onClick={() => setCustomerSelection("BILLING")}
                  title="Faturamento"
                  party={preview.billing}
                />
                <PartyOption
                  selected={customerSelection === "CHARGE"}
                  onClick={() => setCustomerSelection("CHARGE")}
                  title="Cobrança"
                  party={preview.charge}
                />
              </div>

              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox
                  checked={customerSelection === "NEW"}
                  onCheckedChange={(v) => setCustomerSelection(v === true ? "NEW" : "BILLING")}
                />
                Cadastrar novo cliente (preencher dados manualmente)
              </label>

              {customerSelection !== "NEW"
                && !((customerSelection === "BILLING" ? preview.billing : preview.charge).existingCustomerId) && (
                <AlertBox tone="info" title="Cliente não cadastrado">
                  Esse CNPJ ainda não está cadastrado. Preencha os campos abaixo para criar o cliente.
                </AlertBox>
              )}

              {(customerSelection === "NEW"
                || !((customerSelection === "BILLING" ? preview.billing : preview.charge).existingCustomerId)) && (
                <NewCustomerFields value={newCustomer} onChange={setNewCustomer} />
              )}

              {customerSelection !== "NEW"
                && (customerSelection === "BILLING" ? preview.billing : preview.charge).existingCustomerId && (
                <div className="text-sm text-muted-foreground">
                  Cliente identificado:{" "}
                  <strong>
                    {(customerSelection === "BILLING" ? preview.billing : preview.charge).existingCustomerEmail}
                  </strong>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Unidade de entrega</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {preview.deliveryUnit.existingId ? (
                <div className="flex flex-col gap-1 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                    Unidade <strong>{preview.deliveryUnit.externalCode}</strong> — {preview.deliveryUnit.name} já cadastrada
                    <Button
                      variant="ghost"
                      size="sm"
                      className="ml-auto"
                      onClick={() => setUnitSelection(unitSelection === "EXISTING" ? "NEW" : "EXISTING")}
                    >
                      {unitSelection === "EXISTING" ? "Editar dados" : "Usar cadastro existente"}
                    </Button>
                  </div>
                  {preview.deliveryUnit.email && (
                    <div className="pl-6 text-xs">Email: {preview.deliveryUnit.email}</div>
                  )}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">
                  Unidade não cadastrada. Será criada com os dados abaixo.
                </div>
              )}

              {(unitSelection === "NEW" || !preview.deliveryUnit.existingId) && (
                <div className="grid gap-3 md:grid-cols-2">
                  <Labeled label="Código da unidade *">
                    <Input
                      value={newUnit.externalCode}
                      onChange={(e) => setNewUnit({ ...newUnit, externalCode: e.target.value })}
                    />
                  </Labeled>
                  <Labeled label="Nome">
                    <Input
                      value={newUnit.name}
                      onChange={(e) => setNewUnit({ ...newUnit, name: e.target.value })}
                    />
                  </Labeled>
                  <Labeled label="Endereço">
                    <Input
                      value={newUnit.address}
                      onChange={(e) => setNewUnit({ ...newUnit, address: e.target.value })}
                    />
                  </Labeled>
                  <Labeled label="Bairro">
                    <Input
                      value={newUnit.district}
                      onChange={(e) => setNewUnit({ ...newUnit, district: e.target.value })}
                    />
                  </Labeled>
                  <Labeled label="Cidade">
                    <Input
                      value={newUnit.city}
                      onChange={(e) => setNewUnit({ ...newUnit, city: e.target.value })}
                    />
                  </Labeled>
                  <Labeled label="CEP">
                    <Input
                      value={newUnit.postalCode}
                      onChange={(e) => setNewUnit({ ...newUnit, postalCode: e.target.value })}
                    />
                  </Labeled>
                  <Labeled label="Janela de entrega" className="md:col-span-2">
                    <Input
                      value={newUnit.deliveryWindowText}
                      onChange={(e) => setNewUnit({ ...newUnit, deliveryWindowText: e.target.value })}
                    />
                  </Labeled>
                  <Labeled label="Email da unidade" className="md:col-span-2">
                    <Input
                      type="email"
                      value={newUnit.email}
                      onChange={(e) => setNewUnit({ ...newUnit, email: e.target.value })}
                      placeholder="Ex.: compras@unidade.com.br"
                    />
                  </Labeled>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Itens ({preview.items.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {productsLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> Carregando produtos…
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10"></TableHead>
                        <TableHead className="w-12">#</TableHead>
                        <TableHead>Item no arquivo</TableHead>
                        <TableHead className="w-24 text-right">Qtde</TableHead>
                        <TableHead>Produto do catálogo</TableHead>
                        <TableHead className="w-32">Unidade</TableHead>
                        <TableHead className="w-40">Corte</TableHead>
                        <TableHead className="w-32">Lembrar</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {preview.items
                        .filter((item) => !removedLines.has(item.lineNumber))
                        .map((item) => (
                          <ItemRow
                            key={item.lineNumber}
                            item={item}
                            decision={decisions[item.lineNumber]}
                            products={productOptions}
                            onChange={(patch) => updateDecision(item.lineNumber, patch)}
                            onRemove={() => setRemovedLines((prev) => new Set(prev).add(item.lineNumber))}
                            onCreateProduct={() => setProductModal({ lineNumber: item.lineNumber, suggestedName: item.descriptionFromFile ?? "" })}
                          />
                        ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Observações</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Notas internas (opcional)"
                rows={3}
              />
            </CardContent>
          </Card>

          {blockingErrors.length > 0 && (
            <AlertBox tone="error" title="Pendências antes de confirmar">
              <ul className="list-disc pl-5 text-sm">
                {blockingErrors.slice(0, 8).map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
                {blockingErrors.length > 8 && <li>… e mais {blockingErrors.length - 8} ajuste(s).</li>}
              </ul>
            </AlertBox>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancelar</Button>
            {showWizard && (
              <Button variant="outline" onClick={skipCurrent} disabled={submitting}>
                Pular este pedido
              </Button>
            )}
            <Button onClick={handleConfirmClick} disabled={submitting || blockingErrors.length > 0}>
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              {showWizard && currentIndex + 1 < totalMessages
                ? "Confirmar e ir ao próximo"
                : "Confirmar e criar pedido"}
            </Button>
          </div>
        </>
      )}
        </div>
      </DialogContent>

      {productModal && (
        <ProductDialog
          open
          loading={false}
          draft={{ ...emptyDraft, name: productModal.suggestedName.replace(/\s+/g, " ").trim() }}
          categoryOptions={categoryOptions}
          units={catalogMetadata?.units ?? []}
          cutOptions={cutOptions}
          onClose={() => setProductModal(null)}
          onSave={async (data) => {
            try {
              const newProduct = await createAdminProduct(data);
              updateDecision(productModal.lineNumber, {
                catalogProductId: newProduct.id,
                cut: undefined,
                unit: undefined,
              });
              try {
                const list = await fetchAdminProducts();
                setProducts(list.filter((p) => p.active));
              } catch {
                // best-effort; the new product is still selected via decision
              }
              setProductModal(null);
              toast.success(`Produto "${newProduct.name}" criado`);
            } catch (err) {
              toast.error(err instanceof Error ? err.message : "Falha ao criar produto");
            }
          }}
        />
      )}

      <Dialog open={alterationConfirmOpen} onOpenChange={setAlterationConfirmOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Importar AF marcada como alteração?</DialogTitle>
            <DialogDescription>
              {currentMessage?.alterationWarning ??
                "Esta AF está marcada como alteração (ALTERACAO=S), mas está sendo importada como AF nova."}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setAlterationConfirmOpen(false)} disabled={submitting}>
              Cancelar
            </Button>
            <Button
              onClick={() => {
                setAlterationConfirmOpen(false);
                void doConfirm();
              }}
              disabled={submitting}
            >
              Importar mesmo assim
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1 text-sm font-semibold">{value}</div>
    </div>
  );
}

function Labeled({ label, children, className }: { label: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={className}>
      <Label className="text-xs uppercase tracking-wide text-muted-foreground">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function AlertBox({
  tone,
  title,
  children,
}: {
  tone: "error" | "warning" | "info";
  title: string;
  children: React.ReactNode;
}) {
  const palette = tone === "error"
    ? "border-red-300 bg-red-50 text-red-900"
    : tone === "warning"
    ? "border-amber-300 bg-amber-50 text-amber-900"
    : "border-sky-300 bg-sky-50 text-sky-900";
  return (
    <div className={`rounded-lg border p-4 ${palette}`}>
      <div className="mb-2 flex items-center gap-2 font-semibold">
        <AlertCircle className="h-4 w-4" /> {title}
      </div>
      {children}
    </div>
  );
}

function PartyOption({
  selected,
  onClick,
  title,
  party,
}: {
  selected: boolean;
  onClick: () => void;
  title: string;
  party: PartyChoice;
}) {
  const matched = Boolean(party.existingCustomerId);
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left rounded-lg border p-3 transition-colors ${
        selected ? "border-primary bg-primary/5" : "border-border hover:bg-secondary/40"
      }`}
    >
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold">{title}</span>
        {matched ? (
          <Badge variant="default" className="bg-emerald-600">cadastrado</Badge>
        ) : (
          <Badge variant="outline">novo</Badge>
        )}
      </div>
      <div className="mt-2 text-xs text-muted-foreground space-y-0.5">
        <div className="font-medium text-foreground">
          {party.companyName ?? party.existingCustomerName ?? (
            <span className="text-muted-foreground italic">cliente não cadastrado</span>
          )}
        </div>
        <div>CNPJ: {party.cnpj ? formatCnpj(party.cnpj) : "—"}</div>
        {party.address && <div>{party.address}</div>}
        {matched && party.existingCustomerEmail && (
          <div className="text-emerald-700">{party.existingCustomerEmail}</div>
        )}
      </div>
    </button>
  );
}

function NewCustomerFields({
  value,
  onChange,
}: {
  value: NewCustomerForm;
  onChange: (next: NewCustomerForm) => void;
}) {
  const set = <K extends keyof NewCustomerForm>(key: K, v: NewCustomerForm[K]) =>
    onChange({ ...value, [key]: v });

  const [cnpjLooking, setCnpjLooking] = useState(false);

  // Busca os dados da empresa na Receita (BrasilAPI) ao sair do campo CNPJ, igual ao cadastro.
  // Só preenche razão social / nome fantasia quando ainda estão vazios — não sobrescreve o que
  // o operador já digitou ou o que veio pré-preenchido do arquivo.
  const handleCnpjLookup = async () => {
    const digits = normalizeCnpj(value.cnpj);
    if (digits.length !== 14 || !isValidCnpj(digits)) return;
    setCnpjLooking(true);
    try {
      const result = await lookupCnpj(digits);
      if (!result) {
        toast.warning("CNPJ não encontrado na Receita. Preencha os dados manualmente.");
        return;
      }
      onChange({
        ...value,
        companyName: value.companyName.trim() || result.razaoSocial,
        tradeName: value.tradeName.trim() || (result.nomeFantasia ?? ""),
        cnpj: formatCnpj(result.cnpj || digits),
      });
      if (result.situacao && result.situacao.toUpperCase() !== "ATIVA") {
        toast.warning(`Situação cadastral: ${result.situacao}`);
      }
    } catch {
      toast.warning("Não foi possível consultar o CNPJ. Preencha os dados manualmente.");
    } finally {
      setCnpjLooking(false);
    }
  };

  return (
    <div className="grid gap-3 md:grid-cols-2">
      <Labeled label="E-mail *">
        <Input type="email" value={value.email} onChange={(e) => set("email", e.target.value)} />
      </Labeled>
      <Labeled label="Tipo">
        <Select value={value.clientType} onValueChange={(v) => set("clientType", v as ClientType)}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            {CLIENT_TYPES.map((t) => (
              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Labeled>
      <Labeled label="CNPJ">
        <div className="relative">
          <Input
            value={value.cnpj}
            onChange={(e) => set("cnpj", formatCnpj(e.target.value))}
            onBlur={() => void handleCnpjLookup()}
            placeholder="00.000.000/0000-00"
          />
          {cnpjLooking && (
            <Loader2 className="absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
          )}
        </div>
      </Labeled>
      <Labeled label="Razão social">
        <Input value={value.companyName} onChange={(e) => set("companyName", e.target.value)} />
      </Labeled>
      <Labeled label="Nome fantasia">
        <Input value={value.tradeName} onChange={(e) => set("tradeName", e.target.value)} />
      </Labeled>
      <Labeled label="Telefone">
        <Input value={value.phone} onChange={(e) => set("phone", e.target.value)} />
      </Labeled>
      <Labeled label="Contato">
        <Input value={value.contactName} onChange={(e) => set("contactName", e.target.value)} />
      </Labeled>
      <Labeled label="Endereço" className="md:col-span-2">
        <Input value={value.address} onChange={(e) => set("address", e.target.value)} />
      </Labeled>
    </div>
  );
}

/**
 * Lowercases, strips accents, then strips Portuguese gender/plural endings (-a, -o, -as, -os)
 * from each word so "Fatiada", "Fatiado", "Cubos" and "Cubo" all share the same stem.
 */
function normalizeForCutMatch(value: string): string {
  const stripped = value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");
  return stripped.replace(/([a-z])(?:as|os|a|o|s)\b/g, "$1");
}

function detectCutInDescription(description: string | null, availableCuts: string[]): string | null {
  if (!description || availableCuts.length === 0) return null;
  const descNorm = normalizeForCutMatch(description);
  const sorted = [...availableCuts].sort((a, b) => b.length - a.length);
  return sorted.find((cut) => descNorm.includes(normalizeForCutMatch(cut))) ?? null;
}

/** Minúsculas, sem acentos, só letras/dígitos — para casar "Kg" com "KG." vindo do arquivo. */
function normalizeForUnitMatch(value: string): string {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]/g, "");
}

/** Casa a unidade do arquivo com uma das unidades permitidas do produto (por nome normalizado). */
function detectUnitFromFile(unitFromFile: string | null, availableUnits: string[]): string | null {
  if (!unitFromFile || availableUnits.length === 0) return null;
  const target = normalizeForUnitMatch(unitFromFile);
  if (!target) return null;
  return availableUnits.find((u) => normalizeForUnitMatch(u) === target) ?? null;
}

function ProductCombobox({
  products,
  value,
  onChange,
}: {
  products: AdminProductSummary[];
  value: string;
  onChange: (productId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const selected = products.find((p) => p.id === value);
  const sorted = useMemo(
    () => [...products].sort((a, b) => a.name.localeCompare(b.name, "pt-BR")),
    [products],
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "h-9 w-full justify-between bg-background px-2 font-normal",
            !selected && "text-muted-foreground",
          )}
        >
          <span className="min-w-0 truncate">{selected ? selected.name : "— Selecione —"}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] min-w-[260px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Buscar produto..." />
          <CommandList>
            <CommandEmpty>Nenhum produto encontrado.</CommandEmpty>
            <CommandGroup>
              {sorted.map((product) => (
                <CommandItem
                  key={product.id}
                  value={`${product.name} ${product.category}`}
                  onSelect={() => {
                    onChange(product.id);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4 shrink-0",
                      value === product.id ? "opacity-100" : "opacity-0",
                    )}
                  />
                  <span className="truncate">{product.name}</span>
                  <span className="ml-auto shrink-0 pl-2 text-xs text-muted-foreground">
                    {product.category}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function ItemRow({
  item,
  decision,
  products,
  onChange,
  onRemove,
  onCreateProduct,
}: {
  item: ItemPreview;
  decision: ItemDecision | undefined;
  products: AdminProductSummary[];
  onChange: (patch: Partial<ItemDecision>) => void;
  onRemove: () => void;
  onCreateProduct: () => void;
}) {
  const productId = decision?.catalogProductId ?? "";
  const remember = decision?.rememberMapping ?? false;
  const selectedProduct = products.find((p) => p.id === productId);
  const effectiveAvailableCuts = selectedProduct?.availableCuts ?? item.availableCuts;
  const autoSuggestedCut = useMemo(
    () => detectCutInDescription(item.descriptionFromFile, effectiveAvailableCuts),
    [item.descriptionFromFile, effectiveAvailableCuts],
  );
  const cut = decision?.cut ?? "";

  // Unidades permitidas: padrão + alternativas do produto (sem duplicar); cai no que veio do preview.
  const unitOptions = useMemo(() => {
    if (selectedProduct) {
      return Array.from(new Set([selectedProduct.defaultUnit, ...(selectedProduct.availableUnits ?? [])]));
    }
    return item.availableUnits ?? [];
  }, [selectedProduct, item.availableUnits]);
  const autoUnit = useMemo(
    () => detectUnitFromFile(item.unitFromFile, unitOptions) ?? selectedProduct?.defaultUnit ?? null,
    [item.unitFromFile, unitOptions, selectedProduct],
  );
  const unit = decision?.unit ?? "";
  const [showObs, setShowObs] = useState(Boolean(decision?.observation));

  useEffect(() => {
    // Auto-preenche a unidade quando o operador escolhe um produto e ainda não mexeu na unidade.
    // Só quando `decision.unit` é `undefined` (nunca tocado) — não sobrescreve escolha manual.
    if (productId && decision?.unit === undefined && autoUnit) {
      onChange({ unit: autoUnit });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, autoUnit]);

  useEffect(() => {
    // When user picks a product manually and we never set a cut yet, auto-fill from description match.
    // Only auto-fill when `decision.cut` is `undefined` (never touched). If the user explicitly
    // cleared it to `null`, leave it alone.
    if (productId && decision?.cut === undefined && autoSuggestedCut) {
      onChange({ cut: autoSuggestedCut });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, autoSuggestedCut]);

  return (
    <>
    <TableRow className={showObs ? "border-b-0" : undefined}>
      <TableCell className="px-2">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
          title="Remover linha"
          onClick={onRemove}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </TableCell>
      <TableCell className="font-mono text-xs">{item.lineNumber}</TableCell>
      <TableCell className="max-w-md">
        <div className="text-sm font-medium">
          {item.descriptionFromFile ?? <em className="text-muted-foreground">Sem descrição</em>}
        </div>
        <div className="text-xs text-muted-foreground">
          {item.externalCode ? `código ${item.externalCode}` : "sem código"}
          {item.unitFromFile && ` • ${item.unitFromFile}`}
        </div>
        {!showObs && (
          <button
            type="button"
            onClick={() => setShowObs(true)}
            className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
          >
            <Plus className="h-3 w-3" /> Adicionar observação
          </button>
        )}
      </TableCell>
      <TableCell className="text-right tabular-nums">
        <QuantityCell item={item} decision={decision} onChange={onChange} />
      </TableCell>
      <TableCell>
        <ProductCombobox
          products={products}
          value={productId}
          onChange={(id) => onChange({ catalogProductId: id, unit: undefined })}
        />
        {!productId && (
          <div className="mt-1 flex flex-wrap gap-1">
            {item.suggestions.map((s) => (
              <button
                key={s.productId}
                type="button"
                className="text-xs rounded-md border border-border px-2 py-0.5 hover:bg-secondary/60"
                onClick={() => onChange({ catalogProductId: s.productId, unit: undefined })}
              >
                {s.name}
              </button>
            ))}
            <button
              type="button"
              className="text-xs rounded-md border border-emerald-600 bg-emerald-600 px-2 py-0.5 font-medium text-white hover:bg-emerald-700"
              onClick={onCreateProduct}
            >
              + criar produto
            </button>
          </div>
        )}
      </TableCell>
      <TableCell>
        {unitOptions.length === 0 ? (
          <span className="text-xs text-muted-foreground">—</span>
        ) : (
          <select
            value={unit}
            onChange={(e) => onChange({ unit: e.target.value || null })}
            className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
          >
            {!unit && <option value="">— selecione —</option>}
            {unitOptions.map((u) => (
              <option key={u} value={u}>{u}</option>
            ))}
          </select>
        )}
      </TableCell>
      <TableCell>
        {effectiveAvailableCuts.length === 0 ? (
          <span className="text-xs text-muted-foreground">—</span>
        ) : (
          <select
            value={cut}
            onChange={(e) => onChange({ cut: e.target.value || null })}
            className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
          >
            <option value="">— sem corte —</option>
            {effectiveAvailableCuts.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        )}
      </TableCell>
      <TableCell>
        {item.externalCode && (
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <Checkbox
              checked={remember}
              onCheckedChange={(v) => onChange({ rememberMapping: v === true })}
            />
            mapear sempre
          </label>
        )}
      </TableCell>
    </TableRow>
    {showObs && (
      <TableRow className="bg-muted/20 hover:bg-muted/20">
        <TableCell />
        <TableCell colSpan={7} className="pb-3 pt-0">
          <div className="flex items-start gap-2">
            <StickyNote className="mt-2 h-4 w-4 shrink-0 text-amber-600" />
            <Textarea
              autoFocus
              value={decision?.observation ?? ""}
              onChange={(e) => onChange({ observation: e.target.value })}
              placeholder="Observação do item (ex.: separar do restante, embalar à parte)…"
              rows={2}
              className="flex-1 text-sm"
            />
            <button
              type="button"
              onClick={() => {
                onChange({ observation: "" });
                setShowObs(false);
              }}
              className="mt-1.5 shrink-0 text-muted-foreground hover:text-destructive"
              title="Remover observação"
            >
              <XIcon className="h-4 w-4" />
            </button>
          </div>
        </TableCell>
      </TableRow>
    )}
    </>
  );
}

function QuantityCell({
  item,
  decision,
  onChange,
}: {
  item: ItemPreview;
  decision: ItemDecision | undefined;
  onChange: (patch: Partial<ItemDecision>) => void;
}) {
  const override = decision?.quantityOverride;
  const current = override !== undefined ? override : item.quantity ?? null;
  const [draft, setDraft] = useState<string>(current != null ? current.toLocaleString("pt-BR") : "");
  const missing = item.quantity == null;
  const invalid = current == null || current <= 0;

  useEffect(() => {
    if (override === undefined) {
      setDraft(item.quantity != null ? item.quantity.toLocaleString("pt-BR") : "");
    }
  }, [item.quantity, override]);

  const commit = (raw: string) => {
    const parsed = parseQuantityInput(raw);
    onChange({ quantityOverride: parsed });
  };

  return (
    <div className="flex flex-col items-end gap-1">
      <Input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={(e) => commit(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") (e.target as HTMLInputElement).blur();
        }}
        inputMode="decimal"
        placeholder={missing ? "0,000" : ""}
        className={`h-8 w-24 text-right tabular-nums ${invalid ? "border-destructive" : ""}`}
        aria-invalid={invalid}
      />
      {missing && (
        <span className="text-[10px] text-amber-700">não lida do XML</span>
      )}
    </div>
  );
}

